/**
 * Utility functions for working with mentions in TipTap editor
 */

/**
 * Get plain text representation from TipTap editor
 * @param {Editor} editor - TipTap editor instance
 * @returns {string} Plain text content
 */
export function getPlainTextFromEditor(editor) {
  if (!editor) return '';
  return editor.getText();
}

/**
 * Extract mentions from TipTap editor state with indices
 * @param {Editor} editor - TipTap editor instance
 * @returns {Array} Array of mention objects with { userId, display, start, end }
 */
export function extractMentionsFromEditor(editor) {
  if (!editor) return [];
  
  const mentions = [];
  const doc = editor.state.doc;
  
  // Build plain text and track mention positions simultaneously
  let plainTextIndex = 0;
  const mentionNodes = [];
  
  // First pass: collect all mention nodes with their document positions
  doc.descendants((node, pos) => {
    if (node.type.name === 'mention') {
      const userId = node.attrs.id;
      const display = node.attrs.label || node.textContent;
      mentionNodes.push({
        userId,
        display,
        docPos: pos,
        nodeSize: node.nodeSize,
      });
    }
    return true;
  });
  
  // Second pass: calculate plain text indices by traversing and building text
  let currentPlainTextIndex = 0;
  const mentionPositions = new Map();
  
  doc.descendants((node, pos) => {
    if (node.type.name === 'mention') {
      const userId = node.attrs.id;
      const display = node.attrs.label || node.textContent;
      const mentionText = `@${display}`;
      
      const start = currentPlainTextIndex;
      const end = start + mentionText.length;
      
      mentionPositions.set(pos, {
        userId,
        display,
        start,
        end,
      });
      
      currentPlainTextIndex = end;
    } else if (node.isText) {
      currentPlainTextIndex += node.text.length;
    }
    
    return true;
  });
  
  // Convert map to array and sort by start position
  const result = Array.from(mentionPositions.values()).sort((a, b) => a.start - b.start);
  
  return result;
}

/**
 * Extract mentions from plain text using regex (fallback method)
 * @param {string} plainText - Plain text content
 * @param {Editor} editor - TipTap editor instance
 * @returns {Array} Array of mention objects
 */
function extractMentionsFromPlainText(plainText, editor) {
  const mentions = [];
  const mentionRegex = /@(\w+(?:\s+\w+)*)/g;
  const doc = editor.state.doc;
  
  let match;
  while ((match = mentionRegex.exec(plainText)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    const mentionText = match[1].trim();
    
    // Try to find the mention node in the document
    let foundMention = null;
    doc.descendants((node, pos) => {
      if (node.type.name === 'mention') {
        const nodeDisplay = node.attrs.label || node.textContent;
        if (nodeDisplay.toLowerCase().includes(mentionText.toLowerCase())) {
          foundMention = {
            userId: node.attrs.id,
            display: nodeDisplay,
            start,
            end,
          };
          return false; // Stop traversal
        }
      }
      return true;
    });
    
    if (foundMention) {
      mentions.push(foundMention);
    }
  }
  
  return mentions;
}

/**
 * Find mention node at a specific position in the editor
 * @param {Editor} editor - TipTap editor instance
 * @param {number} pos - Position in the document
 * @returns {Object|null} Mention node and position, or null
 */
export function findMentionAtPosition(editor, pos) {
  if (!editor) return null;
  
  const { $from } = editor.state.selection;
  let mentionNode = null;
  let mentionPos = null;
  
  // Check if we're inside a mention node
  $from.node(-1).descendants((node, nodePos) => {
    if (node.type.name === 'mention') {
      const start = nodePos;
      const end = nodePos + node.nodeSize;
      if (pos >= start && pos <= end) {
        mentionNode = node;
        mentionPos = nodePos;
        return false; // Stop traversal
      }
    }
    return true;
  });
  
  if (mentionNode) {
    return {
      node: mentionNode,
      pos: mentionPos,
      start: mentionPos,
      end: mentionPos + mentionNode.nodeSize,
    };
  }
  
  return null;
}

/**
 * Check if cursor is at a mention boundary
 * @param {Editor} editor - TipTap editor instance
 * @returns {Object|null} Information about the mention boundary, or null
 */
export function isMentionBoundary(editor) {
  if (!editor) return null;
  
  const { $from, $to } = editor.state.selection;
  const pos = $from.pos;
  
  // Check if we're at the start of a mention
  const mentionAtPos = findMentionAtPosition(editor, pos);
  if (mentionAtPos) {
    const isAtStart = pos === mentionAtPos.start;
    const isAtEnd = pos === mentionAtPos.end;
    
    return {
      isBoundary: isAtStart || isAtEnd,
      isAtStart,
      isAtEnd,
      mention: mentionAtPos,
    };
  }
  
  // Check if we're right before a mention
  const nodeAfter = $from.nodeAfter;
  if (nodeAfter && nodeAfter.type.name === 'mention') {
    return {
      isBoundary: true,
      isAtStart: false,
      isAtEnd: true,
      mention: {
        node: nodeAfter,
        pos: $from.pos,
        start: $from.pos,
        end: $from.pos + nodeAfter.nodeSize,
      },
    };
  }
  
  // Check if we're right after a mention
  const nodeBefore = $from.nodeBefore;
  if (nodeBefore && nodeBefore.type.name === 'mention') {
    return {
      isBoundary: true,
      isAtStart: true,
      isAtEnd: false,
      mention: {
        node: nodeBefore,
        pos: $from.pos - nodeBefore.nodeSize,
        start: $from.pos - nodeBefore.nodeSize,
        end: $from.pos,
      },
    };
  }
  
  return null;
}

/**
 * Get all mentions from editor state as a simple array
 * @param {Editor} editor - TipTap editor instance
 * @returns {Array} Array of { userId, display } objects
 */
export function getMentionsList(editor) {
  if (!editor) return [];
  
  const mentions = [];
  const doc = editor.state.doc;
  
  doc.descendants((node) => {
    if (node.type.name === 'mention') {
      mentions.push({
        userId: node.attrs.id,
        display: node.attrs.label || node.textContent,
      });
    }
    return true;
  });
  
  return mentions;
}

